import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Label,
  Tab,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

/** Format a field name for display: snake_case -> Title Case */
const formatField = (name) =>
  name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Render a definition table from an object (key-value pairs) */
const DefinitionTable = ({ data, exclude = [] }) => {
  if (!data) return null;
  const entries = Object.entries(data).filter(
    ([k]) => !exclude.includes(k) && !k.endsWith('_at') && k !== 'id',
  );
  if (entries.length === 0) return <p>No data.</p>;
  return (
    <Table definition compact>
      <Table.Body>
        {entries.map(([key, value]) => (
          <Table.Row key={key}>
            <Table.Cell width={4}>{formatField(key)}</Table.Cell>
            <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>
              {value === null || value === undefined
                ? ''
                : typeof value === 'boolean'
                  ? value
                    ? 'Yes'
                    : 'No'
                  : String(value)}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

/** Overview tab: base language_conventions row */
const OverviewPane = ({ base }) => (
  <Tab.Pane>
    {base ? (
      <DefinitionTable
        data={base}
        exclude={['language_code', 'created_by', 'updated_by', 'reviewed_by']}
      />
    ) : (
      <p>No convention data for this language.</p>
    )}
  </Tab.Pane>
);

/** Grammar patterns tab */
const GrammarPane = ({ rows }) => (
  <Tab.Pane>
    <Table compact celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Pattern Name</Table.HeaderCell>
          <Table.HeaderCell>Type</Table.HeaderCell>
          <Table.HeaderCell>Description</Table.HeaderCell>
          <Table.HeaderCell>Example (Source)</Table.HeaderCell>
          <Table.HeaderCell>Example (Target)</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell><strong>{r.pattern_name}</strong></Table.Cell>
            <Table.Cell><Label size="small">{r.pattern_type}</Label></Table.Cell>
            <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>{r.description}</Table.Cell>
            <Table.Cell><code>{r.example_source}</code></Table.Cell>
            <Table.Cell><code>{r.example_target}</code></Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  </Tab.Pane>
);

/** Plural rules tab */
const PluralsPane = ({ rows }) => (
  <Tab.Pane>
    <Table compact celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Category</Table.HeaderCell>
          <Table.HeaderCell>Rule</Table.HeaderCell>
          <Table.HeaderCell>Example Numbers</Table.HeaderCell>
          <Table.HeaderCell>English</Table.HeaderCell>
          <Table.HeaderCell>Translated</Table.HeaderCell>
          <Table.HeaderCell>ICU Pattern</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell><Label size="small">{r.category}</Label></Table.Cell>
            <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>{r.rule_description}</Table.Cell>
            <Table.Cell>{r.example_numbers}</Table.Cell>
            <Table.Cell>{r.example_english}</Table.Cell>
            <Table.Cell>{r.example_translated}</Table.Cell>
            <Table.Cell><code>{r.icu_pattern_example}</code></Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  </Tab.Pane>
);

/** Punctuation rules tab */
const PunctuationPane = ({ rows }) => (
  <Tab.Pane>
    <Table compact celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Rule Type</Table.HeaderCell>
          <Table.HeaderCell>Character</Table.HeaderCell>
          <Table.HeaderCell>Space Before</Table.HeaderCell>
          <Table.HeaderCell>Space After</Table.HeaderCell>
          <Table.HeaderCell>Context</Table.HeaderCell>
          <Table.HeaderCell>Correct</Table.HeaderCell>
          <Table.HeaderCell>Incorrect</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell><Label size="small">{r.rule_type}</Label></Table.Cell>
            <Table.Cell style={{ fontSize: '1.2em' }}><code>{r.character_standard}</code></Table.Cell>
            <Table.Cell textAlign="center">{r.space_before ? 'Yes' : 'No'}</Table.Cell>
            <Table.Cell textAlign="center">{r.space_after ? 'Yes' : 'No'}</Table.Cell>
            <Table.Cell>{r.context}</Table.Cell>
            <Table.Cell>{r.example_correct}</Table.Cell>
            <Table.Cell style={{ color: '#db2828' }}>{r.example_incorrect}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  </Tab.Pane>
);

/** Calendar terms tab — split by term_type */
const CalendarPane = ({ rows }) => {
  const types = [...new Set(rows.map((r) => r.term_type))];
  return (
    <Tab.Pane>
      {types.map((type) => {
        const typeRows = rows.filter((r) => r.term_type === type);
        const hasDeclensions = typeRows.some(
          (r) => r.genitive || r.locative,
        );
        return (
          <div key={type} style={{ marginBottom: '1.5em' }}>
            <Header as="h4">{formatField(type)}</Header>
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>#</Table.HeaderCell>
                  <Table.HeaderCell>English</Table.HeaderCell>
                  <Table.HeaderCell>Translated</Table.HeaderCell>
                  {hasDeclensions && (
                    <>
                      <Table.HeaderCell>Nominative</Table.HeaderCell>
                      <Table.HeaderCell>Genitive</Table.HeaderCell>
                      <Table.HeaderCell>Locative</Table.HeaderCell>
                    </>
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {typeRows.map((r) => (
                  <Table.Row key={r.id}>
                    <Table.Cell>{r.term_index}</Table.Cell>
                    <Table.Cell>{r.english_term}</Table.Cell>
                    <Table.Cell><strong>{r.translated_term}</strong></Table.Cell>
                    {hasDeclensions && (
                      <>
                        <Table.Cell>{r.nominative}</Table.Cell>
                        <Table.Cell>{r.genitive}</Table.Cell>
                        <Table.Cell>{r.locative}</Table.Cell>
                      </>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        );
      })}
    </Tab.Pane>
  );
};

/** Ordinal suffixes tab */
const OrdinalsPane = ({ rows }) => (
  <Tab.Pane>
    <Table compact celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Pattern</Table.HeaderCell>
          <Table.HeaderCell>Suffix</Table.HeaderCell>
          <Table.HeaderCell>Masculine</Table.HeaderCell>
          <Table.HeaderCell>Feminine</Table.HeaderCell>
          <Table.HeaderCell>Neuter</Table.HeaderCell>
          <Table.HeaderCell>Example</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell><code>{r.number_pattern}</code></Table.Cell>
            <Table.Cell><strong>{r.suffix}</strong></Table.Cell>
            <Table.Cell>{r.suffix_masculine}</Table.Cell>
            <Table.Cell>{r.suffix_feminine}</Table.Cell>
            <Table.Cell>{r.suffix_neuter}</Table.Cell>
            <Table.Cell>{r.example}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  </Tab.Pane>
);

/** Special characters tab — inline display */
const CharactersPane = ({ rows }) => (
  <Tab.Pane>
    <p>
      {rows.length} special character{rows.length !== 1 ? 's' : ''} in sort
      order:
    </p>
    <Segment>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em' }}>
        {rows.map((r) => (
          <Label key={r.id} size="large" basic>
            <span style={{ fontSize: '1.3em' }}>{r.character}</span>
            <Label.Detail>#{r.sort_order}</Label.Detail>
          </Label>
        ))}
      </div>
    </Segment>
  </Tab.Pane>
);

/** Sources + Attributions tab */
const SourcesPane = ({ sources, attributions }) => (
  <Tab.Pane>
    {sources.length > 0 && (
      <>
        <Header as="h4">Authoritative Sources</Header>
        <Table compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Priority</Table.HeaderCell>
              <Table.HeaderCell>Source</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Organization</Table.HeaderCell>
              <Table.HeaderCell>Purpose</Table.HeaderCell>
              <Table.HeaderCell>URL</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sources.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell textAlign="center">{r.priority}</Table.Cell>
                <Table.Cell><strong>{r.source_name}</strong></Table.Cell>
                <Table.Cell><Label size="small">{r.source_type}</Label></Table.Cell>
                <Table.Cell>{r.organization}</Table.Cell>
                <Table.Cell>{r.purpose}</Table.Cell>
                <Table.Cell>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noopener noreferrer">
                      Link
                    </a>
                  ) : (
                    ''
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </>
    )}
    {attributions.length > 0 && (
      <>
        <Header as="h4" style={{ marginTop: '1.5em' }}>
          Convention Attributions
        </Header>
        <Table compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Field</Table.HeaderCell>
              <Table.HeaderCell>Source</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Organization</Table.HeaderCell>
              <Table.HeaderCell>Verified</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {attributions.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell><strong>{formatField(r.field_name)}</strong></Table.Cell>
                <Table.Cell>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noopener noreferrer">
                      {r.source_name}
                    </a>
                  ) : (
                    r.source_name
                  )}
                </Table.Cell>
                <Table.Cell><Label size="small">{r.source_type}</Label></Table.Cell>
                <Table.Cell>{r.organization}</Table.Cell>
                <Table.Cell>{r.verified_date || ''}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </>
    )}
    {sources.length === 0 && attributions.length === 0 && (
      <p>No source data for this language.</p>
    )}
  </Tab.Pane>
);

/** Severity label colors */
const severityColor = (s) =>
  s === 'must' ? 'red' : s === 'should' ? 'yellow' : 'grey';
const domainColor = (d) =>
  d === 'english_source' ? 'blue' : 'teal';

/** Audit rules tab */
const AuditRulesPane = ({ rows }) => {
  // Group by rule_code to show one row per rule with check info
  const ruleMap = new Map();
  rows.forEach((r) => {
    if (!ruleMap.has(r.rule_code)) {
      ruleMap.set(r.rule_code, {
        rule_code: r.rule_code,
        title: r.title,
        severity: r.severity,
        category: r.category,
        description: r.description,
        domain: r.domain,
        testability: r.testability,
        scope: r.scope,
        checks: [],
      });
    }
    if (r.check_type) {
      ruleMap.get(r.rule_code).checks.push({
        check_type: r.check_type,
        check_target: r.check_target,
        check_definition: r.check_definition,
        example_pass: r.example_pass,
        example_fail: r.example_fail,
        notes: r.check_notes,
      });
    }
  });
  const rules = [...ruleMap.values()];

  return (
    <Tab.Pane>
      <p>{rules.length} audit rule{rules.length !== 1 ? 's' : ''} applicable.</p>
      <Table compact celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Rule</Table.HeaderCell>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell>Domain</Table.HeaderCell>
            <Table.HeaderCell>Severity</Table.HeaderCell>
            <Table.HeaderCell>Category</Table.HeaderCell>
            <Table.HeaderCell>Testability</Table.HeaderCell>
            <Table.HeaderCell>Checks</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rules.map((r) => (
            <Table.Row key={r.rule_code}>
              <Table.Cell><code>{r.rule_code}</code></Table.Cell>
              <Table.Cell>
                <strong>{r.title}</strong>
                <div
                  style={{
                    fontSize: '0.85em',
                    color: '#666',
                    marginTop: '0.3em',
                  }}
                >
                  {r.description}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Label size="small" color={domainColor(r.domain)}>
                  {r.domain === 'english_source' ? 'source' : 'translation'}
                </Label>
              </Table.Cell>
              <Table.Cell>
                <Label size="small" color={severityColor(r.severity)}>
                  {r.severity}
                </Label>
              </Table.Cell>
              <Table.Cell>{r.category}</Table.Cell>
              <Table.Cell>{r.testability}</Table.Cell>
              <Table.Cell textAlign="center">
                {r.checks.length > 0 ? (
                  <Label size="small" color="green" circular>
                    {r.checks.length}
                  </Label>
                ) : (
                  <Label size="small" basic circular>
                    0
                  </Label>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Tab.Pane>
  );
};

const ConventionsDetail = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchApi(`${getApiPath()}/@polyglot/conventions/${code}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [code]);

  if (loading) {
    return (
      <Container id="polyglot-conventions">
        <Loader active inline="centered" content="Loading conventions..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container id="polyglot-conventions">
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const {
    base,
    grammar_patterns,
    plural_rules,
    punctuation_rules,
    calendar_terms,
    ordinal_suffixes,
    special_characters,
    authoritative_sources,
    attributions,
    audit_rules,
  } = data;

  // Build panes — only include tabs that have data (Overview always shows)
  const panes = [
    {
      menuItem: 'Overview',
      render: () => <OverviewPane base={base} />,
    },
  ];

  if (grammar_patterns && grammar_patterns.length > 0) {
    panes.push({
      menuItem: `Grammar (${grammar_patterns.length})`,
      render: () => <GrammarPane rows={grammar_patterns} />,
    });
  }
  if (plural_rules && plural_rules.length > 0) {
    panes.push({
      menuItem: `Plurals (${plural_rules.length})`,
      render: () => <PluralsPane rows={plural_rules} />,
    });
  }
  if (punctuation_rules && punctuation_rules.length > 0) {
    panes.push({
      menuItem: `Punctuation (${punctuation_rules.length})`,
      render: () => <PunctuationPane rows={punctuation_rules} />,
    });
  }
  if (calendar_terms && calendar_terms.length > 0) {
    panes.push({
      menuItem: `Calendar (${calendar_terms.length})`,
      render: () => <CalendarPane rows={calendar_terms} />,
    });
  }
  if (ordinal_suffixes && ordinal_suffixes.length > 0) {
    panes.push({
      menuItem: `Ordinals (${ordinal_suffixes.length})`,
      render: () => <OrdinalsPane rows={ordinal_suffixes} />,
    });
  }
  if (special_characters && special_characters.length > 0) {
    panes.push({
      menuItem: `Characters (${special_characters.length})`,
      render: () => <CharactersPane rows={special_characters} />,
    });
  }
  if (
    (authoritative_sources && authoritative_sources.length > 0) ||
    (attributions && attributions.length > 0)
  ) {
    const count =
      (authoritative_sources ? authoritative_sources.length : 0) +
      (attributions ? attributions.length : 0);
    panes.push({
      menuItem: `Sources (${count})`,
      render: () => (
        <SourcesPane
          sources={authoritative_sources || []}
          attributions={attributions || []}
        />
      ),
    });
  }
  if (audit_rules && audit_rules.length > 0) {
    panes.push({
      menuItem: `Audit Rules (${audit_rules.length})`,
      render: () => <AuditRulesPane rows={audit_rules} />,
    });
  }

  const nativeName = base?.language_name_native || code;
  const englishName = base?.language_name_english || '';

  return (
    <Container
      id="polyglot-conventions"
      style={{ paddingTop: '2em', paddingBottom: '2em' }}
    >
      <Helmet title={`${nativeName} Conventions - Polyglot`} />
      <div style={{ marginBottom: '0.5em' }}>
        <Link to={`/polyglot/languages/${code}`}>
          &larr; Back to {nativeName}
        </Link>
      </div>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        {nativeName} &mdash; Conventions
        <Header.Subheader>
          {englishName && englishName !== nativeName
            ? `${englishName} (${code})`
            : code}
        </Header.Subheader>
      </Header>

      <Tab panes={panes} />
    </Container>
  );
};

export default ConventionsDetail;
